import React, { useState, useEffect, useRef } from "react";
import { FcGoogle } from "react-icons/fc";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  LoginIcon,
  EmailSection,
  EmailInput,
  VerificationMessage,
  CodeInputContainer,
  CodeInput,
  ResendButton,
  ModalSubtitle,
  TitleContainer,
  ChevronButton,
  GoogleButton,
  LabelContainer,
  Label,
  TelegramButton,
} from "./AuthModal.styles";
import { useDispatch, useSelector } from "react-redux";
import { sendCode, verifyEmail } from "../../store/slices/auth/asyncThunks";
import { toast } from "../modern-ui/sonner";
import {
  selectLoginPending,
  selectVerifyEmailLoading,
} from "../../store/slices/auth/slice";
import MailChecker from "mailchecker";
import { selectAffiliateRef } from "../../store/slices/common/slice";
import CustomTurnstile from "../../components/utils/CustomTurnstile";
import { AUTH_TYPE } from "../../utils/constants";
import { useGoogleLogin } from "@react-oauth/google";
import { ReactComponent as LogoSvg } from "../../assets/icons/fullLogo.svg";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as TelegramSvg } from "../../assets/icons/telegram.svg";
import { ReactComponent as EditSvg } from "../../assets/icons/edit.svg";
import Button from "../utils/Button/Button";
import CustomDiscord from "../utils/CustomDiscord/CustomDiscord";
import { useSearchParams } from "react-router-dom";
import Modal from "../utils/Modal/Modal";
import { useTelegramLogin } from "@telegram-login-ultimate/react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";

const AuthModal = ({ isOpen, onClose }) => {
  useLockBodyScroll(isOpen);
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [token, setToken] = useState(null);
  const [telegramData, setTelegramData] = useState(null);

  const loginLoading = useSelector(selectLoginPending);
  const verifyEmailLoading = useSelector(selectVerifyEmailLoading);

  const affiliateRef = useSelector(selectAffiliateRef);
  const inputRefs = useRef([]);
  const turnstileRef = useRef(null);

  const discordCodeParam = searchParams.get("code");
  const discordStateParam = searchParams.get("state");

  const dispatch = useDispatch();

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      value = value[0];
    }

    if (!/^\d*$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "Enter" && code.every((digit) => digit !== "")) {
      handleVerifyCode();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);

    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    const newCode = [...code];
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);

    const nextEmptyIndex = newCode.findIndex((digit) => !digit);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const onGoogleSignUpClick = useGoogleLogin({
    flow: "auth-code",
    onSuccess: ({ code }) => {
      dispatch(
        sendCode({
          googleCode: code,
          authType: AUTH_TYPE.GOOGLE,
          ...(affiliateRef && { affiliateRef }),
        })
      )
        .unwrap()
        .then((response) => {
          if (response?.isNewUser) {
            setShowCodeInput(false);
            toast.success("Please set your username.");
            onClose();
          } else {
            toast.success("Successfully signed in with Google!");
            onClose();
          }
        })
        .catch((err) => {
          toast.error(
            err.message || "Google sign-in failed. Please try again."
          );
        })
        .finally(() => {
          turnstileRef.current?.reset();
          setToken(null);
        });
    },
    onError: (err) => {
      toast.error("Google sign-in failed");
      console.error(err);
    },
  });

  const handleSendCode = () => {
    if (!email) {
      toast.error("Please enter an email address");
      return;
    }

    if (!MailChecker.isValid(email) || email.includes("+")) {
      toast.error("Please enter a valid email address");
      return;
    }

    dispatch(sendCode({ email, token, authType: AUTH_TYPE.BASIC }))
      .unwrap()
      .then((res) => {
        setShowCodeInput(true);
        toast.success(`Verification code sent to ${email}`);
      })
      .catch((err) => {
        toast.error(
          "Failed to send code. Please try again or use another email."
        );
      })
      .finally(() => {
        turnstileRef.current?.reset();
        setToken(null);
      });
  };

  useEffect(() => {
    const codeString = code.join("");
    if (codeString.trim().length === 6) {
      dispatch(
        verifyEmail({
          email,
          code: codeString,
          ...(affiliateRef && { affiliateRef }),
        })
      )
        .unwrap()
        .then((response) => {
          if (response?.data?.isNewUser) {
            setShowCodeInput(false);
            onClose();
            toast.success("Code verified! Please set your username.");
          } else {
            toast.success("Successfully signed in!");
            onClose();
          }
        })
        .catch((err) => {
          toast.error(err.message || "Invalid code. Please try again.");
        });
    }
    // eslint-disable-next-line
  }, [code]);

  const handleVerifyCode = () => {
    const codeString = code.join("");

    if (codeString.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    dispatch(
      verifyEmail({
        email,
        code: codeString,
        ...(affiliateRef && { affiliateRef }),
      })
    )
      .unwrap()
      .then((response) => {
        if (response?.data?.isNewUser) {
          setShowCodeInput(false);
          onClose();
          toast.success("Code verified! Please set your username.");
        } else {
          toast.success("Successfully signed in!");
          onClose();
        }
      })
      .catch((err) => {
        toast.error(err.message || "Invalid code. Please try again.");
      });
  };

  const handleDiscordError = (error) => {
    toast.error(error || "Discord login failed. Please try again.");
    turnstileRef.current?.reset();
    setToken(null);
  };

  const handleResendCode = () => {
    setCode(["", "", "", "", "", ""]);
    handleSendCode();
  };

  const handleBackToEmail = () => {
    setShowCodeInput(false);
    setCode(["", "", "", "", "", ""]);
  };

  useEffect(() => {
    if (showCodeInput) {
      const raf = requestAnimationFrame(() => {
        inputRefs.current[0]?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [showCodeInput]);

  useEffect(() => {
    if (telegramData) {
      dispatch(
        sendCode({
          telegramData,
          authType: AUTH_TYPE.TELEGRAM,
          ...(affiliateRef && { affiliateRef }),
        })
      )
        .unwrap()
        .then((response) => {
          if (response?.isNewUser) {
            onClose();
            toast.success("Please set your username.");
          } else {
            onClose();
            setTelegramData(null);
            toast.success("Successfully signed in!");
          }
        })
        .catch((err) => {
          toast.error(err.message);
        })
        .finally(() => {
          setToken(null);
        });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telegramData]);

  useEffect(() => {
    if (discordCodeParam) {
      const finalAffiliate = affiliateRef || discordStateParam;
      dispatch(
        sendCode({
          discordCode: discordCodeParam,
          authType: AUTH_TYPE.DISCORD,
          ...(finalAffiliate && { affiliateRef: finalAffiliate }),
        })
      )
        .unwrap()
        .then((response) => {
          if (response?.isNewUser) {
            onClose();
            toast.success("Please set your username.");
          } else {
            onClose();
            toast.success("Successfully signed in!");
          }
        })
        .catch((err) => {
          toast.error(err.message);
        })
        .finally(() => {
          searchParams.delete("code");
          searchParams.delete("state");
          setSearchParams(searchParams);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discordCodeParam]);

  const [openPopup, { isPending }] = useTelegramLogin({
    botId: "8250817360",
    onSuccess: (user) => setTelegramData(user),
    onFail: () => {
      toast.error(
        "Couldn’t open Telegram login. Check your connection and try again."
      );
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={() => {
        setEmail("");
        setCode(["", "", "", "", "", ""]);
        setShowCodeInput(false);
      }}
    >
      <ModalContainer $width={showCodeInput}>
        <CloseButton
          onClick={() => {
            onClose();
            setTelegramData(null);
          }}
        >
          <CloseSvg />
        </CloseButton>
        {showCodeInput && (
          <ChevronButton
            onClick={() => {
              setShowCodeInput(false);
              setCode(["", "", "", "", "", ""]);
            }}
          >
            <ChevronSvg />
          </ChevronButton>
        )}
        <ModalContent>
          <LogoSvg />
          <TitleContainer>
            <ModalTitle>
              {showCodeInput ? "Enter Verification Code" : "Login"}
            </ModalTitle>
            {showCodeInput && (
              <>
                <ModalSubtitle>We sent a 6-digit code to</ModalSubtitle>
                <ModalSubtitle style={{ opacity: 1 }}>
                  {email}{" "}
                  <EditSvg
                    onClick={handleBackToEmail}
                    style={{ marginLeft: 8, cursor: "pointer" }}
                  />
                </ModalSubtitle>

                <ResendButton onClick={handleResendCode}>
                  Resend Code
                </ResendButton>
              </>
            )}
          </TitleContainer>
          {showCodeInput ? (
            <VerificationMessage>
              <CodeInputContainer>
                {code.map((digit, index) => (
                  <CodeInput
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    maxLength={1}
                  />
                ))}
              </CodeInputContainer>
              <Button
                onClick={handleVerifyCode}
                disabled={code.some((digit) => !digit)}
                title={"Continue"}
                loading={verifyEmailLoading}
                style={{ width: "100%" }}
              />
              <CustomTurnstile
                invisible
                ref={turnstileRef}
                siteKey="0x4AAAAAAB3oOsLeJ72Dzuq1"
                onSuccess={(token) => setToken(token)}
                onError={() => toast.error("Verification failed")}
                onExpire={() => {
                  setToken(null);
                  toast.error("Verification expired");
                }}
              />
            </VerificationMessage>
          ) : (
            <>
              {!discordCodeParam && (
                <EmailSection>
                  <TelegramButton
                    disabled={isPending || loginLoading}
                    onClick={openPopup}
                  >
                    <TelegramSvg width={20} />
                    {isPending ? "Loading..." : "Log in with Telegram"}
                  </TelegramButton>

                  <GoogleButton
                    onClick={onGoogleSignUpClick}
                    disabled={loginLoading}
                  >
                    <LoginIcon>
                      <FcGoogle size={18} />
                    </LoginIcon>
                    Log in with Google
                  </GoogleButton>
                  <CustomDiscord
                    onError={handleDiscordError}
                    disabled={loginLoading}
                    affiliateRef={affiliateRef}
                  />
                  {!telegramData && (
                    <LabelContainer>
                      <Label>or use email address</Label>
                      <EmailInput
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === "Enter" && handleSendCode()
                        }
                      />
                    </LabelContainer>
                  )}
                </EmailSection>
              )}
              <CustomTurnstile
                ref={turnstileRef}
                siteKey="0x4AAAAAAB3oOsLeJ72Dzuq1"
                onSuccess={(token) => setToken(token)}
                onError={() => toast.error("Verification failed")}
                onExpire={() => {
                  setToken(null);
                  toast.error("Verification expired");
                }}
              />
              <Button
                onClick={handleSendCode}
                disabled={!(email || telegramData)}
                title={"Send Verification Code"}
                loading={loginLoading}
                style={{ width: "100%" }}
              />
            </>
          )}
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default AuthModal;
